// The Spend panel above Admin → Config → AI/LLM: what SMART's own Claude calls cost
// (`GET /admin/llm/usage`, plan claude-llm.md §5). Track only: SMART sets no cap, so the
// panel reads, it never refuses. Every cost is an estimate from the catalog's list prices,
// never Anthropic's invoice, and an attempt whose cost is unknown is counted beside the
// sum, never read as zero. Models are named from `@/lib/claude-models`; LLM spend is
// SMART's own cost, so the screen this sits on is the Admin's alone.

import { useAdminLlmUsage } from '@/api/config-hooks';
import type { LlmLastCall, LlmUsageResponse, LlmUsageTotals } from '@/api/types';
import { QueryErrorNote } from '@/components/error-panel';
import { ScrollTable } from '@/components/scroll-table';
import { claudeModelLabel } from '@/lib/claude-models';
import { fmtDateShort, fmtDateTime, fmtDay, withEt } from '@/lib/datetime';

const TASK_LABELS: Readonly<Record<string, string>> = {
  daily_briefing: 'Daily Briefing',
  explain: 'Explain',
};

/** What each `llm_calls.outcome` means to the person reading it. */
const OUTCOME_WORDS: Readonly<Record<string, string>> = {
  ok: 'Answered',
  refused: 'Declined by Claude',
  truncated: 'Cut off at its token budget',
  invalid: 'Answered with something unusable',
  'error:timeout': 'Timed out',
  'error:config': 'Not sent: the API key is missing or not a Claude key',
  'error:rate_limit_error': 'Rate limited, or the monthly spend limit was reached',
  'error:overloaded_error': 'Claude was overloaded',
  'error:authentication_error': 'The API key was refused',
  'error:permission_error': 'The API key may not use this model',
  'error:invalid_request_error': 'Refused as an invalid request',
  'error:api_error': 'Failed on Claude’s side',
};

export function taskLabel(task: string): string {
  return TASK_LABELS[task] ?? task.replaceAll('_', ' ');
}

export function outcomeWords(outcome: string): string {
  const known = OUTCOME_WORDS[outcome];
  if (known) return known;
  if (outcome.startsWith('error:transport_')) return 'Could not reach Claude';
  const http = /^error:http_(\d{3})$/.exec(outcome);
  if (http) return `Failed (HTTP ${http[1]})`;
  return 'Failed';
}

/** `$1.23` from micro-dollars, to the cent; a cost above zero but under a cent reads `<$0.01`. */
export function usdFromMicros(micros: number): string {
  if (micros > 0 && micros < 5_000) return '<$0.01';
  const cents = Math.round(micros / 10_000);
  return `$${Math.floor(cents / 100).toLocaleString('en-US')}.${String(cents % 100).padStart(2, '0')}`;
}

/** `+ 2 calls of unknown cost`, or null when every cost is known. */
export function unknownCostNote(totals: LlmUsageTotals): string | null {
  const n = totals.unknown_cost_calls;
  return n > 0 ? `+ ${n.toLocaleString('en-US')} call${n === 1 ? '' : 's'} of unknown cost` : null;
}

function promptTokens(totals: LlmUsageTotals): number {
  return totals.input_tokens + totals.cache_write_5m_tokens + totals.cache_write_1h_tokens
    + totals.cache_read_tokens;
}

function count(n: number): string {
  return n.toLocaleString('en-US');
}

function share(ratio: number | null): string {
  return ratio === null ? '—' : `${Math.round(ratio * 100)}%`;
}

export function LlmSpendPanel() {
  const query = useAdminLlmUsage();
  return (
    <section className="admin-llm-spend" aria-labelledby="admin-llm-spend-title">
      <div className="admin-llm-spend-head">
        <h3 id="admin-llm-spend-title">Claude spend</h3>
        <p>
          What the Daily Briefing and the Explain panels cost, estimated from list prices.
          SMART sets no cap: the monthly spend limit is the one on the Claude Console.
        </p>
      </div>
      {query.error ? (
        <QueryErrorNote error={query.error} what="Claude spend" onRetry={() => void query.refetch()} />
      ) : !query.data ? (
        <div className="admin-llm-spend-empty mono" role="status">Loading Claude spend…</div>
      ) : (
        <LlmSpendView usage={query.data} />
      )}
    </section>
  );
}

export function LlmSpendView({ usage }: { usage: LlmUsageResponse }) {
  // Every recorded attempt is a last call: none at all is the empty state.
  if (usage.last_call === null) {
    return (
      <div className="admin-llm-spend-empty">
        <strong>No Claude calls yet.</strong>
        <span>The first Daily Briefing or Explain with an API key set shows its cost here.</span>
      </div>
    );
  }
  return (
    <>
      <dl className="admin-llm-spend-totals">
        <SpendTotal label="Month to date" sub={`since ${fmtDateShort(usage.month_start)}`} totals={usage.month_to_date} />
        <SpendTotal label="Last 30 days" sub="" totals={usage.last_30_days} />
        <LastCall call={usage.last_call} />
      </dl>
      <p className="admin-llm-spend-note">
        Estimated from list prices read {fmtDay(usage.prices_as_of)}, not Anthropic’s invoice.
      </p>
      <UsageTable
        caption="This month by task"
        nameHeader="Task"
        rows={usage.by_task.map((row) => ({ key: row.task, name: taskLabel(row.task), usage: row.usage }))}
      />
      <UsageTable
        caption="This month by model"
        nameHeader="Model"
        rows={usage.by_model.map((row) => ({
          key: row.model,
          // A model the catalog does not price keeps its id, marked, so it reads as a gap
          // to fill, not as a name.
          name: claudeModelLabel(row.model) ?? `${row.model} (no list price)`,
          usage: row.usage,
        }))}
      />
    </>
  );
}

function SpendTotal({ label, sub, totals }: { label: string; sub: string; totals: LlmUsageTotals }) {
  const unknown = unknownCostNote(totals);
  const calls = `${count(totals.calls)} call${totals.calls === 1 ? '' : 's'}`;
  return (
    <div className="admin-llm-spend-total">
      <dt>{label}</dt>
      <dd>
        <span className="admin-llm-spend-value">{usdFromMicros(totals.cost_micros)}</span>
        <span className="admin-llm-spend-sub">{sub ? `${calls} ${sub}` : calls}</span>
        {unknown && <span className="admin-llm-spend-unknown">{unknown}</span>}
      </dd>
    </div>
  );
}

function LastCall({ call }: { call: LlmLastCall | null }) {
  return (
    <div className="admin-llm-spend-total">
      <dt>Last call</dt>
      <dd>
        {call ? (
          <>
            <span className="admin-llm-spend-when">{withEt(fmtDateTime(call.at))}</span>
            <span className="admin-llm-spend-sub">{taskLabel(call.task)} · {outcomeWords(call.outcome)}</span>
          </>
        ) : (
          <span className="admin-llm-spend-sub">None recorded</span>
        )}
      </dd>
    </div>
  );
}

interface UsageRow {
  key: string;
  name: string;
  usage: LlmUsageTotals;
}

function UsageTable({ caption, nameHeader, rows }: { caption: string; nameHeader: string; rows: UsageRow[] }) {
  return (
    <div className="admin-llm-spend-table">
      <h4>{caption}</h4>
      {rows.length === 0 ? (
        <p className="admin-llm-spend-note">No calls this month.</p>
      ) : (
        <ScrollTable label={`Claude spend: ${caption.toLowerCase()}`} identityColumn={1}>
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{nameHeader}</th>
                <th scope="col" className="num">Calls</th>
                <th scope="col" className="num">Prompt tokens</th>
                <th scope="col" className="num">Output tokens</th>
                <th scope="col" className="num">Cache hits</th>
                <th scope="col" className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const unknown = unknownCostNote(row.usage);
                return (
                  <tr key={row.key}>
                    <td className="name">{row.name}</td>
                    <td className="num">{count(row.usage.calls)}</td>
                    <td className="num">{count(promptTokens(row.usage))}</td>
                    <td className="num">{count(row.usage.output_tokens)}</td>
                    <td className="num">{share(row.usage.cache_hit_share)}</td>
                    <td className="num">
                      {usdFromMicros(row.usage.cost_micros)}
                      {unknown && <span className="admin-llm-spend-unknown">{unknown}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollTable>
      )}
    </div>
  );
}
